import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Body, Button } from '@e2n/ui/v2';
import { useTranslation } from 'react-i18next';
import type { ChatHistoryState } from '../hooks/useChatHistory';
import { formatSessionTime } from '../lib/groupSessions';
import type { SessionGroup } from '../lib/groupSessions';

export function ChatHistoryGroup({
  group,
  history,
}: {
  group: SessionGroup;
  history: ChatHistoryState;
}) {
  const { t, i18n } = useTranslation();

  return (
    <section className="flex flex-col pb-2">
      <Body
        asChild
        size="sm"
        weight="semibold"
        className="px-2 pt-2 pb-1 text-labels-secondary">
        <h3>{t(`domain.ai.chat.history.groups.${group.key}`)}</h3>
      </Body>
      <ul className="flex flex-col">
        {group.sessions.map((session) => (
          <SessionItem
            key={session.id}
            title={history.titleOf(session)}
            meta={formatSessionTime(
              session.lastMessageAt,
              group.key,
              i18n.language,
            )}
            isActive={session.id === history.activeSessionId}
            // Während eine Antwort läuft, lässt sich nur der aktive Chat öffnen
            openDisabled={
              history.isBusy ||
              (history.isStreaming && session.id !== history.activeSessionId)
            }
            deleteDisabled={history.isBusy}
            deleteLabel={t('domain.ai.chat.history.delete')}
            onOpen={() => void history.open(session)}
            onDelete={() => void history.removeSession(session)}
          />
        ))}
      </ul>
    </section>
  );
}

type SessionItemProps = {
  title: string;
  meta: string;
  isActive: boolean;
  openDisabled: boolean;
  deleteDisabled: boolean;
  deleteLabel: string;
  onOpen: () => void;
  onDelete: () => void;
};

// Öffnen und Löschen sind zwei getrennte Buttons, nebeneinander statt ineinander
function SessionItem({
  title,
  meta,
  isActive,
  openDisabled,
  deleteDisabled,
  deleteLabel,
  onOpen,
  onDelete,
}: SessionItemProps) {
  return (
    <li
      className={`group/session flex items-center rounded-lg focus-within:bg-neutrals-v2-200 hover:bg-neutrals-v2-200 ${isActive ? 'bg-neutrals-v2-200' : ''}`}>
      <button
        type="button"
        disabled={openDisabled}
        aria-current={isActive || undefined}
        onClick={onOpen}
        className="flex min-w-0 flex-1 cursor-pointer flex-col items-start gap-0.5 rounded-lg px-2 py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-neutrals-v2-600 disabled:cursor-not-allowed">
        <Body className="w-full truncate">{title}</Body>
        <Body size="sm" className="text-labels-secondary">
          {meta}
        </Body>
      </button>
      <Button
        variant="ghost"
        size="small"
        iconOnly
        disabled={deleteDisabled}
        aria-label={deleteLabel}
        onClick={onDelete}
        className="mr-1 shrink-0 md:opacity-0 md:group-focus-within/session:opacity-100 md:group-hover/session:opacity-100">
        <FontAwesomeIcon icon={faTrashCan} className="size-4" />
      </Button>
    </li>
  );
}
